// @core/stores/context/ContextStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { contextApi } from '@core/api';
import type {
  AspectTypeManifest,
  GetContextLayoutResponse,
  GetTenantContextConfigResponse,
  GetUserContextPreferencesResponse,
  RegisterAspectManifestRequest,
  RegisterAspectManifestResponse,
  RegisterRelationGroupManifestRequest,
  RegisterRelationGroupManifestResponse,
  TrackBehaviorRequest,
  TrackBehaviorResponse,
  UpdateTenantContextConfigRequest,
  UpdateTenantContextConfigResponse,
  UpdateUserContextPreferencesRequest,
  UpdateUserContextPreferencesResponse,
} from '@core/types/context';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ContextStore {
  // List state
  items: AspectTypeManifest[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: AspectTypeManifest | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, AspectTypeManifest>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): AspectTypeManifest | undefined {
    return this.cache.get(id);
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private updateCache(_item: AspectTypeManifest | undefined): void {
    // Entity type does not have entity.id field — override with custom cache key logic
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Get Context Panel layout for an entity
   */
  async GetContextLayout(entity_type: string, entity_id: string): Promise<GetContextLayoutResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.GetContextLayout(entity_type, entity_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant context configuration
   */
  async GetTenantContextConfig(params?: { }): Promise<GetTenantContextConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.GetTenantContextConfig(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get user context preferences
   */
  async GetUserContextPreferences(params?: { }): Promise<GetUserContextPreferencesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.GetUserContextPreferences(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List all registered manifests
   */
  async ListManifests(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await contextApi.ListManifests(params);

      runInAction(() => {
        this.items = response.aspect_manifests?.manifests ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register aspect type manifest
   */
  async RegisterAspectManifest(aspect_type: string, data: RegisterAspectManifestRequest): Promise<RegisterAspectManifestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.RegisterAspectManifest(aspect_type, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register relation group manifest
   */
  async RegisterRelationGroupManifest(group_key: string, data: RegisterRelationGroupManifestRequest): Promise<RegisterRelationGroupManifestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.RegisterRelationGroupManifest(group_key, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Track user behavior for context scoring
   */
  async TrackBehavior(data: TrackBehaviorRequest): Promise<TrackBehaviorResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.TrackBehavior(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant context configuration
   */
  async UpdateTenantContextConfig(data: UpdateTenantContextConfigRequest): Promise<UpdateTenantContextConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.UpdateTenantContextConfig(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user context preferences
   */
  async UpdateUserContextPreferences(data: UpdateUserContextPreferencesRequest): Promise<UpdateUserContextPreferencesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await contextApi.UpdateUserContextPreferences(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const contextStore = new ContextStore();
